// 매장 디폴트값(tenants.formDefaults)과 판매점 정보(retailer_info) 사이의 매핑 헬퍼.
//
// 키 규약: tenants.formDefaults 의 키는 "라벨" 이다 (서식 템플릿 placeholder 와 공유).
// 반면 retailer_info 의 키는 RetailerField.key 다. 두 키 공간을 라벨로 이어준다.

import type { RetailerField } from "../../../shared/retailer-form-fields";

/** "비어있는 값"으로 간주하는지 — null/undefined/빈문자열/빈배열. */
function isEmpty(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** RetailerField 의 kind 별로 formDefaults 의 문자열을 적절한 타입으로 변환. */
function coerceByKind(field: RetailerField, raw: string): unknown {
  switch (field.kind) {
    case "checkbox":
      // 콤마/세미콜론 구분 문자열을 배열로. 빈 토큰 제거.
      return raw
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    case "calc":
    case "file":
      // 계산·파일은 디폴트 의미가 약함 — 그래도 들어와 있다면 문자열 그대로.
      return raw;
    case "number":
      // 모달의 NumberInput 은 문자열도 받아서 콤마 포맷한다.
      return raw;
    default:
      return raw;
  }
}

export interface ApplyOptions {
  /** 현재 retailer_info 값 (key=RetailerField.key) */
  current: Record<string, unknown>;
  /** 매장 디폴트값 (key=라벨) */
  formDefaults: Record<string, string>;
  /** 적용 대상 필드 카탈로그 (전체 평탄화) */
  fields: RetailerField[];
  /**
   * fill-empty: 비어있는 항목만 채움 (기본)
   * overwrite : 라벨에 디폴트가 있으면 무조건 덮어씀
   */
  mode?: "fill-empty" | "overwrite";
}

export interface ApplyResult {
  next: Record<string, unknown>;
  filledCount: number;
}

/**
 * 매장 디폴트값을 retailer_info 형식으로 변환해 적용한다.
 * 라벨 → 키 매핑으로 동작하므로, 라벨이 카탈로그에 없는 디폴트값은 무시한다.
 */
export function applyRetailerDefaults(opts: ApplyOptions): ApplyResult {
  const { current, formDefaults, fields, mode = "fill-empty" } = opts;
  const next: Record<string, unknown> = { ...current };
  let filledCount = 0;

  for (const field of fields) {
    const raw = formDefaults[field.label];
    if (raw == null || raw === "") continue;

    const shouldFill = mode === "overwrite" || isEmpty(next[field.key]);
    if (!shouldFill) continue;

    next[field.key] = coerceByKind(field, raw);
    filledCount++;
  }

  return { next, filledCount };
}

/** retailer_info 값을 formDefaults 의 문자열 표현으로 직렬화. */
export function serializeForDefaults(_field: RetailerField, raw: unknown): string {
  if (raw == null) return "";
  if (Array.isArray(raw)) return raw.join(", ");
  if (typeof raw === "boolean") return raw ? "예" : "아니오";
  return String(raw);
}
